// Database types for AOI Gaming PCs
// MERGED - includes all enhancements

// Re-exported so this stays the single definition. The list previously here
// omitted 'manager' and 'technician', both of which exist in the database
// ENUM and are checked throughout the app.
export type { UserRole } from '@/lib/roles';
export type BuildStatus = 'Active' | 'Inactive' | 'eol' | 'archived' | 'prototype';
export type ComponentStatus = 'active' | 'discontinued' | 'out_of_stock';
export type MovementType = 'stock_in' | 'stock_out' | 'adjustment' | 'reserved' | 'unreserved' | 'consumed';
export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock';

// UPDATED - Added 'awaiting_review'
export type OrderStatus = 
  | 'pending' 
  | 'awaiting_review' 
  | 'picking' 
  | 'building' 
  | 'testing' 
  | 'completed' 
  | 'cancelled';

export type ReservationStatus =
  | 'Pending'
  | 'In Progress'
  | 'Partial'
  | 'Completed'
  | 'Cancelled';

// NEW - Component types for reservation_components
export type ComponentType = 
  | 'cpu' 
  | 'cpu_cooler' 
  | 'mb' 
  | 'ram' 
  | 'rom_1' 
  | 'rom_2' 
  | 'rom_3'
  | 'gpu' 
  | 'psu' 
  | 'os' 
  | 'case' 
  | 'acc' 
  | 'acc_2' 
  | 'acc_3' 
  | 'acc_4' 
  | 'acc_5' 
  | 'acc_6' 
  | 'acc_7';

// Base types
export interface Supplier {
  id: string;
  name: string;
  code: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ComponentTypeDefinition {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  specs_schema: Record<string, unknown>;
}

export interface ComponentGroup {
  id: string;
  name: string;
  component_type_id: string | null;
  description: string | null;
  created_at: string;
}

export interface Component {
  id: string;
  part_number: string;
  description: string;
  normalized_sku: string | null;
  component_type_id: string | null;
  group_id: string | null;
  is_preferred_in_group: boolean;
  supplier_id: string | null;
  cost: number;
  total_qty: number;
  available_qty: number;
  on_order_qty: number;
  reserved_qty: number;
  expected_qty: number;
  threshold: number;
  specs: Record<string, unknown>;
  status: ComponentStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComponentWithDetails extends Component {
  type_code: string | null;
  type_name: string | null;
  supplier_name: string | null;
  group_name: string | null;
  stock_status: StockStatus;
}

export interface BuildFamily {
  id: string;
  code: string;
  name: string;
  is_custom: boolean;
  sort_order: number;
}

export interface Client {
  id: string;
  name: string;
  initials: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
}

export interface Build {
  id: string;
  sku: string;
  description: string;
  family_id: string | null;
  generation: string | null;
  revision: string | null;
  client_id: string | null;
  status: BuildStatus;
  total_cost: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  build_families?: { name: string };  // For Supabase nested queries
}

export interface BuildWithDetails extends Build {
  family_name: string | null;
  family_code: string | null;
  is_custom: boolean;
  client_name: string | null;
  client_initials: string | null;
  max_buildable: number;
  active_reservations: number;
}

export interface BuildComponent {
  id: string;
  build_id: string;
  component_id: string | null;
  component_group_id: string | null;
  quantity: number;
  part_type: string;
  sort_order: number;
  notes: string | null;
}

export interface BuildComponentWithDetails extends BuildComponent {
  component?: Component;
  component_group?: ComponentGroup;
}

export interface Technician {
  id: string;
  user_id: string | null;
  name: string;
  surname: string | null;
  initials: string | null;
  // Set independently of user_id so a technician can be notified before their
  // first sign-in. See 20260810_technicians_email.sql.
  email: string | null;
  is_active: boolean;
  created_at: string;
}

// UPDATED - Enhanced Reservation with fulfillment tracking
export interface Reservation {
  id: string;
  reservation_code: string;
  build_id: string | null;
  build_sku: string;
  reservation_date: string | null;
  quantity: number;
  quantity_fulfilled: number;
  quantity_remaining?: number;
  technician_id?: string | null;
  status: string;
  priority?: number;
  notes: string | null;
  is_legacy?: boolean;
  components?: Record<string, any> | null;
  component_quantities_csv?: string | null;
  source?: string;
  created_at: string;
  updated_at?: string;
  confirmed_at?: string | null;
  completed_at?: string | null;
  created_by?: string | null;
}

export interface ReservationWithDetails extends Reservation {
  build?: Build;
  builds?: Build;  // Supabase foreign key uses plural
  technician?: Technician;
  technicians?: Technician;  // Supabase foreign key uses plural
  items?: ReservationItem[];
  components_list?: ReservationComponent[];  // NEW
  fulfillment_percentage?: number;            // NEW
}

export interface ReservationItem {
  id: string;
  reservation_id: string;
  component_id: string;
  quantity_reserved: number;
}

// NEW - Reservation Component interface
export interface ReservationComponent {
  id: string;
  reservation_id: string;
  component_type: ComponentType;
  part_number: string | null;
  description: string | null;
  quantity: number;                 // Required quantity
  fulfilled_quantity: number;        // How many allocated
  created_at: string;
  updated_at: string;
}

// NEW - Component fulfillment detail
export interface ComponentFulfillment extends ReservationComponent {
  reservation_code: string;
  remaining_quantity: number;
  fulfillment_percentage: number;
  fulfillment_status: 'pending' | 'partial' | 'complete';
}

// NEW - Reservation fulfillment summary (from view)
export interface ReservationFulfillment {
  id: string;
  reservation_code: string;
  total_quantity: number;
  quantity_fulfilled: number;
  quantity_remaining: number;
  status: ReservationStatus;
  build_sku: string | null;
  build_description: string | null;
  family_name: string | null;
  technician_name: string | null;
  technician_initials: string | null;
  created_at: string;
  confirmed_at: string | null;
  completed_at: string | null;
  component_count: number;
  components_fulfilled: number;
  fulfillment_percentage: number;
}

export interface AssemblyOrder {
  id: string;
  order_number: string;
  build_id: string;
  reservation_id: string | null;
  quantity: number;
  technician_id: string | null;
  status: OrderStatus;
  to_number: string | null;
  legacy_iir: string | null;
  notes: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  created_by: string | null;
}

export interface AssemblyOrderWithDetails extends AssemblyOrder {
  build?: Build;
  reservation?: Reservation;
  technician?: Technician;
  items?: AssemblyOrderItem[];
}

export interface AssemblyOrderItem {
  id: string;
  build_order_id: string;
  component_id: string;
  quantity: number;
  picked: boolean;
  picked_at: string | null;
}

export interface StockMovement {
  id: string;
  component_id: string;
  movement_type: MovementType;
  quantity: number;
  previous_qty: number | null;
  new_qty: number | null;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

export interface ImportLog {
  id: string;
  filename: string | null;
  import_type: string | null;
  status: string;
  records_total: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  errors: unknown[];
  created_at: string;
  completed_at: string | null;
  created_by: string | null;
}

// UPDATED - Enhanced DashboardSummary with more metrics
export interface DashboardSummary {
  // Component metrics
  total_components: number;
  low_stock_count: number;
  out_of_stock_count: number;
  
  // Build metrics
  active_builds: number;
  
  // Reservation metrics - ENHANCED
  pending_reservations: number;
  in_progress_reservations: number;
  partially_fulfilled_reservations: number;  // NEW
  units_pending: number;                     // NEW - total units waiting
  
  // Build Order (PAO) metrics
  active_orders: number;
  pending_orders: number;
  awaiting_review_orders: number;
  in_production_orders: number;
  completed_this_week: number;
  completed_this_month: number;
  
  // Production metrics
  units_built_this_week: number;
  units_built_this_month: number;
  
  // Technician metrics
  active_technicians: number;
}

// NEW - Technician workload view
export interface TechnicianWorkload {
  technician_id: string;
  technician_name: string;
  initials: string;
  active_orders: number;
  pending_reservations: number;
  partial_reservations: number;      // NEW
  units_this_week: number;
  units_this_month: number;
}

// NEW - Parse Excel quantities helper type
export interface ExcelReservationData {
  reservation_id: string;
  date: string;
  build_sku: string;
  reserved_qty: number;
  status: string;
  components: {
    cpu?: string;
    cpu_cooler?: string;
    mb?: string;
    ram?: string;
    rom_1?: string;
    rom_2?: string;
    rom_3?: string;
    gpu?: string;
    psu?: string;
    os?: string;
    case?: string;
    acc?: string;
    acc_2?: string;
    acc_3?: string;
    acc_4?: string;
    acc_5?: string;
    acc_6?: string;
    acc_7?: string;
  };
  quantities_csv: string; // "1,1,1,1,2,0,0,0,1,1,1,1,1,0,0,0,0,0,0"
}

// ── APM (AOI Performance Metric) ────────────────────────────────────────────
export interface BuildBenchmark {
  id: string;
  build_id: string;
  passmark_rating: number | null;
  cpu_mark: number | null;
  graphics_2d_mark: number | null;
  graphics_3d_mark: number | null;
  memory_mark: number | null;
  disk_mark: number | null;
  sample_count: number;
  updated_at: string;
}

export interface AssemblyOrderBenchmark {
  id: string;
  assembly_order_id: string;
  unit_number: number;
  technician_id: string | null;
  passmark_rating: number;
  cpu_mark: number | null;
  graphics_2d_mark: number | null;
  graphics_3d_mark: number | null;
  memory_mark: number | null;
  disk_mark: number | null;
  created_at: string;
}

// Form types
export interface ComponentFormData {
  part_number: string;
  description: string;
  normalized_sku?: string;
  component_type_id?: string;
  supplier_id?: string;
  cost: number;
  available_qty: number;
  on_order_qty: number;
  threshold: number;
  specs?: Record<string, unknown>;
  notes?: string;
}

export interface BuildFormData {
  sku: string;
  description: string;
  family_id?: string;
  generation?: string;
  revision?: string;
  client_id?: string;
  status: BuildStatus;
  notes?: string;
}

export interface ReservationFormData {
  reservation_code?: string;
  build_id: string;
  quantity: number;
  technician_id?: string;
  status: ReservationStatus;
  priority?: number;
  notes?: string;
  components?: Record<ComponentType, {
    part_number?: string;
    quantity: number;
  }>;
}

export interface ReservationComponentFormData {
  component_type: ComponentType;
  part_number?: string;
  description?: string;
  quantity: number;
}

export interface AssemblyOrderFormData {
  build_id: string;
  reservation_id?: string;
  quantity: number;
  technician_id?: string;
  to_number?: string;
  legacy_iir?: string;
  notes?: string;
}

// ============================================
// CONSTANTS & HELPER FUNCTIONS
// ============================================

export const RESERVATION_STATUS_COLORS: Record<ReservationStatus, string> = {
  'Pending': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-orange-100 text-orange-800',
  'Partial': 'bg-purple-100 text-purple-800',
  'Completed': 'bg-green-100 text-green-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

export const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  'Pending':     'Pending',
  'In Progress': 'In Progress',
  'Partial':     'Partial',
  'Completed':   'Completed',
  'Cancelled':   'Cancelled',
};

export const COMPONENT_TYPE_LABELS: Record<ComponentType, string> = {
  cpu: 'CPU',
  cpu_cooler: 'CPU Cooler',
  mb: 'Motherboard',
  ram: 'RAM',
  rom_1: 'Storage 1',
  rom_2: 'Storage 2',
  rom_3: 'Storage 3',
  gpu: 'Graphics Card',
  psu: 'Power Supply',
  os: 'Operating System',
  case: 'Case',
  acc: 'Accessory 1',
  acc_2: 'Accessory 2',
  acc_3: 'Accessory 3',
  acc_4: 'Accessory 4',
  acc_5: 'Accessory 5',
  acc_6: 'Accessory 6',
  acc_7: 'Accessory 7',
};

/**
 * Parse CSV quantities string into array of numbers
 */
export function parseQuantitiesCSV(csv: string): number[] {
  return csv.split(',').map(q => parseInt(q.trim(), 10) || 0);
}

/**
 * Map component types to part numbers based on quantities
 */
export function mapComponentsToQuantities(
  components: ExcelReservationData['components'],
  quantities: number[]
): Record<ComponentType, { part_number: string; quantity: number }> {
  const componentTypes: ComponentType[] = [
    'cpu', 'cpu_cooler', 'mb', 'ram', 'rom_1', 'rom_2', 'rom_3',
    'gpu', 'psu', 'os', 'case', 'acc', 'acc_2', 'acc_3', 'acc_4',
    'acc_5', 'acc_6', 'acc_7'
  ];

  const result: any = {};
  
  componentTypes.forEach((type, index) => {
    const partNumber = components[type];
    const quantity = quantities[index] || 0;
    
    if (partNumber && quantity > 0) {
      result[type] = {
        part_number: partNumber,
        quantity
      };
    }
  });

  return result;
}

/**
 * Calculate fulfillment percentage
 */
export function calculateFulfillmentPercentage(fulfilled: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((fulfilled / total) * 100);
}

/**
 * Get status color class
 */
export function getReservationStatusColor(status: ReservationStatus): string {
  return RESERVATION_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Get human-readable status label
 */
export function getReservationStatusLabel(status: ReservationStatus): string {
  return RESERVATION_STATUS_LABELS[status] || status;
}

/**
 * Get component type display label
 */
export function getComponentTypeLabel(type: ComponentType): string {
  return COMPONENT_TYPE_LABELS[type] || type;
}

/**
 * Canonical component category sort order.
 * CPU → FAN → RAM → MB → ROM → GPU → PSU → OS → CAS → COO → ACC
 * Used everywhere builds are displayed (reservations, assembly orders, configurator, etc.)
 */
export const COMPONENT_SORT_ORDER: Record<string, number> = {
  cpu: 0, CPU: 0,
  cpu_cooler: 1, fan: 1, FAN: 1, cooler: 1, COOLER: 1, 'cpu cooler': 1,
  ram: 2, RAM: 2, MEMORY: 2, memory: 2,
  mb: 3, MB: 3, MOBO: 3, mobo: 3, MOTHERBOARD: 3, motherboard: 3,
  rom: 4, ROM: 4, rom_1: 4, ROM_1: 4, rom_2: 4.1, ROM_2: 4.1, rom_3: 4.2, ROM_3: 4.2, storage: 4, STORAGE: 4, SSD: 4, ssd: 4, HDD: 4, hdd: 4, NVME: 4, nvme: 4,
  gpu: 5, GPU: 5, VGA: 5, vga: 5, GRAPHICS: 5, graphics: 5,
  psu: 6, PSU: 6, POWER: 6, power: 6,
  os: 7, OS: 7, WINDOWS: 7, windows: 7,
  case: 8, cas: 8, CAS: 8, CASE: 8, CHASSIS: 8, chassis: 8, TOWER: 8, tower: 8, ENCLOSURE: 8, enclosure: 8,
  coo: 9, COO: 9,
  acc: 10, ACC: 10, acc_2: 10.1, ACC_2: 10.1, acc_3: 10.2, ACC_3: 10.2, acc_4: 10.3, ACC_4: 10.3, acc_5: 10.4, ACC_5: 10.4, acc_6: 10.5, ACC_6: 10.5, acc_7: 10.6, ACC_7: 10.6,
  accessories: 10, ACCESSORIES: 10,
};

/** Get sort rank for a component type/category string */
export function getComponentSortRank(type: string): number {
  if (type in COMPONENT_SORT_ORDER) return COMPONENT_SORT_ORDER[type];
  const lower = type.toLowerCase();
  if (lower in COMPONENT_SORT_ORDER) return COMPONENT_SORT_ORDER[lower];
  if (lower.startsWith('rom')) return 4;
  if (lower.startsWith('acc')) return 10;
  return 99;
}

/**
 * Map Excel status to database status
 */
export function mapExcelStatusToDb(excelStatus: string): ReservationStatus {
  const statusMap: Record<string, ReservationStatus> = {
    'Reserved':    'Pending',
    'Cancelled':   'Cancelled',
    'Completed':   'Completed',
    'In Progress': 'In Progress',
    'Partial':     'Partial',
  };

  return statusMap[excelStatus] || 'Pending';
}

/**
 * Determine if reservation can be edited
 */
export function isReservationEditable(status: ReservationStatus): boolean {
  return !['Completed', 'Cancelled'].includes(status);
}

/**
 * Determine if reservation can be cancelled
 */
export function isReservationCancellable(status: ReservationStatus): boolean {
  return !['Completed', 'Cancelled'].includes(status);
}
